import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Settings, Plus, X, Save } from 'lucide-react';
import { axiosInstance } from '../../lib/api-client';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { DebugTokenPanel } from './DebugTokenPanel';
import { MarketingUserPermissionsManager } from './MarketingUserPermissionsManager';
import { BulkPromotionalOrderCreator } from './BulkPromotionalOrderCreator';

interface Settings {
  _id?: string;
  maxDiscountPercentages: number[];
  maxExtraContacts: number[];
  maxExtraValidity: number[];
  commissionPercentage: number;
}

export function AdminPromotionalOrderSettings() {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<Settings>({
    maxDiscountPercentages: [10, 15, 20, 25, 30, 35, 40, 50],
    maxExtraContacts: [0, 5, 10, 15, 20, 25, 30, 50],
    maxExtraValidity: [0, 7, 15, 30, 45, 60, 90],
    commissionPercentage: 10
  });

  const [tempValues, setTempValues] = useState({
    discount: '',
    contacts: '',
    validity: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get('/promotional-orders/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error: any) {
      toast.error('Failed to fetch settings');
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axiosInstance.patch(
        '/promotional-orders/settings',
        settings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Settings saved successfully');
      }
    } catch (error: any) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const addValue = (field: 'discount' | 'contacts' | 'validity') => {
    const value = Number(tempValues[field]);
    if (!value || value <= 0) {
      toast.error('Please enter a valid positive number');
      return;
    }

    let arrayField: keyof Settings;
    if (field === 'discount') arrayField = 'maxDiscountPercentages';
    else if (field === 'contacts') arrayField = 'maxExtraContacts';
    else arrayField = 'maxExtraValidity';

    const currentArray = settings[arrayField] as number[];
    if (currentArray.includes(value)) {
      toast.error('This value already exists');
      return;
    }

    setSettings({
      ...settings,
      [arrayField]: [...currentArray, value].sort((a, b) => a - b)
    });

    setTempValues({ ...tempValues, [field]: '' });
  };

  const removeValue = (field: 'maxDiscountPercentages' | 'maxExtraContacts' | 'maxExtraValidity', value: number) => {
    setSettings({
      ...settings,
      [field]: (settings[field] as number[]).filter(v => v !== value)
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-rose-500" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Promotional Order Settings</h2>
            <p className="text-sm text-gray-600">Configure available options for marketing team</p>
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-rose-500 hover:bg-rose-600"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Discount Percentages */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Maximum Discount Percentages</h3>
          <p className="text-sm text-gray-600 mb-4">
            Available discount options for marketing team (in percentage)
          </p>
          
          <div className="flex gap-2 mb-4">
            <Input
              type="number"
              placeholder="e.g., 45"
              value={tempValues.discount}
              onChange={(e) => setTempValues({ ...tempValues, discount: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && addValue('discount')}
            />
            <Button onClick={() => addValue('discount')}>Add</Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {settings.maxDiscountPercentages.map((percent) => (
              <div
                key={percent}
                className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full flex items-center gap-2"
              >
                <span>{percent}%</span>
                <button
                  onClick={() => removeValue('maxDiscountPercentages', percent)}
                  className="hover:text-rose-900"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Extra Contacts */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Maximum Extra Contacts</h3>
          <p className="text-sm text-gray-600 mb-4">
            Available bonus contacts options for marketing team
          </p>
          
          <div className="flex gap-2 mb-4">
            <Input
              type="number"
              placeholder="e.g., 35"
              value={tempValues.contacts}
              onChange={(e) => setTempValues({ ...tempValues, contacts: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && addValue('contacts')}
            />
            <Button onClick={() => addValue('contacts')}>Add</Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {settings.maxExtraContacts.map((contacts) => (
              <div
                key={contacts}
                className="px-3 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-2"
              >
                <span>{contacts === 0 ? 'None' : `+${contacts}`}</span>
                <button
                  onClick={() => removeValue('maxExtraContacts', contacts)}
                  className="hover:text-green-900"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Extra Validity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Maximum Extra Validity (Days)</h3>
          <p className="text-sm text-gray-600 mb-4">
            Available bonus validity options for marketing team
          </p>
          
          <div className="flex gap-2 mb-4">
            <Input
              type="number"
              placeholder="e.g., 120"
              value={tempValues.validity}
              onChange={(e) => setTempValues({ ...tempValues, validity: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && addValue('validity')}
            />
            <Button onClick={() => addValue('validity')}>Add</Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {settings.maxExtraValidity.map((days) => (
              <div
                key={days}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full flex items-center gap-2"
              >
                <span>{days === 0 ? 'None' : `+${days} days`}</span>
                <button
                  onClick={() => removeValue('maxExtraValidity', days)}
                  className="hover:text-blue-900"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Commission Percentage */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Commission Percentage</h3>
          <p className="text-sm text-gray-600 mb-4">
            Commission rate for marketing team on promotional orders
          </p>
          
          <div className="flex items-center gap-4">
            <Input
              type="number"
              min="0"
              max="100"
              value={settings.commissionPercentage}
              onChange={(e) => setSettings({ ...settings, commissionPercentage: Number(e.target.value) })}
              className="max-w-xs"
            />
            <span className="text-2xl font-bold text-orange-600">{settings.commissionPercentage}%</span>
          </div>

          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800">
              <strong>Example:</strong> For a ₹1000 order, marketing team earns ₹{(1000 * settings.commissionPercentage / 100).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Allow Marketing Managers to Create */}
        {/* REMOVED - Now managed per-user in Marketing User Permissions Manager below */}
      </div>

      <DebugTokenPanel />
      <MarketingUserPermissionsManager />
      <BulkPromotionalOrderCreator />
    </div>
  );
}